import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, LifeBuoy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('🛡️ AgendaCRM Error Recovery:', error, errorInfo);
        
        // 📡 L5 NATIVE: Telemetría remota asíncrona no bloqueante
        try {
            // Importación dinámica para no bloquear el hilo de renderizado principal
            import('@sentry/react').then(Sentry => {
                Sentry.captureException(error, { extra: errorInfo as unknown as Record<string, unknown> });
            });

            // Fallback de contingencia (L5 Logger Backend)
            import('../../api/client').then(({ default: client }) => {
                client.post('/telemetry/crash-report', {
                    message: error.message,
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    url: window.location.href,
                    timestamp: new Date().toISOString()
                }).catch(() => null); // Silent kill si la red falló
            });
        } catch (e) {
            // Failsafe absoluto
        }
    }

    private handleReset = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#050505] text-white p-6 relative overflow-hidden font-sans">
                    {/* Background Glows */}
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 rounded-full blur-[120px]" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-red-500/10 rounded-full blur-[120px]" />

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-xl w-full bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-10 md:p-16 text-center shadow-2xl relative z-10"
                    >
                        {/* Icon with Ring Animation */}
                        <div className="relative mx-auto w-24 h-24 mb-10">
                            <motion.div
                                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="absolute inset-0 bg-red-500/20 rounded-full blur-xl"
                            />
                            <div className="relative w-full h-full bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-500">
                                <AlertTriangle className="w-12 h-12 text-white" />
                            </div>
                        </div>

                        <h2 className="text-3xl md:text-4xl font-black mb-4 tracking-tight bg-gradient-to-r from-white via-white to-zinc-500 bg-clip-text text-transparent">
                            Algo no salió como esperábamos
                        </h2>

                        <p className="text-zinc-400 text-lg mb-10 leading-relaxed font-medium">
                            El sistema ha encontrado una anomalía crítica. No te preocupes, el equipo de ingeniería ha sido notificado y tus datos están seguros.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                            <button
                                onClick={() => window.location.href = window.location.href}
                                className="flex items-center justify-center gap-3 px-6 py-4 bg-white text-black font-bold rounded-2xl transition-all hover:bg-zinc-200 active:scale-95"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Reintentar
                            </button>
                            <button
                                onClick={this.handleReset}
                                className="flex items-center justify-center gap-3 px-6 py-4 bg-white/5 border border-white/10 text-white font-bold rounded-2xl transition-all hover:bg-white/10 active:scale-95"
                            >
                                <Home className="w-5 h-5" />
                                Ir al Inicio
                            </button>
                        </div>

                        <div className="pt-8 border-t border-white/5 flex flex-col items-center">
                            <a
                                href="mailto:support@agendacrm.com"
                                className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-6 group"
                            >
                                <LifeBuoy className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                                <span>Hablar con Soporte Técnico</span>
                            </a>

                            <AnimatePresence>
                                {this.state.error && (
                                    <motion.details
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="group w-full"
                                    >
                                        <summary className="text-xs text-zinc-700 cursor-pointer list-none hover:text-zinc-500 transition-colors uppercase tracking-widest font-bold">
                                            Detalles Técnicos para Ingeniería
                                        </summary>
                                        <pre className="mt-4 text-[10px] text-left bg-black/40 p-4 rounded-xl overflow-auto max-h-40 text-red-400 border border-red-500/20 font-mono scrollbar-hide">
                                            {this.state.error.stack || this.state.error.toString()}
                                        </pre>
                                    </motion.details>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            );
        }

        return this.props.children;
    }
}
